import { supabase } from "@/integrations/supabase/client";
import { ConsoleLogsSettings } from "@/types/adminSettings";

let cachedSettings: ConsoleLogsSettings | null = null;
let settingsLoadPromise: Promise<ConsoleLogsSettings> | null = null;

const defaultConsoleSettings: ConsoleLogsSettings = {
  indexPage: {
    componentRendering: false,
    authFlow: false,
    networkStatus: false,
    termsCheck: false,
    errorHandling: true,
    performanceMetrics: false,
  },
  performance: {
    performanceMonitor: false,
    fontLoading: false,
  },
  auth: {
    authContext: false,
  },
  admin: {
    useAdminSettings: false,
  },
  debugging: {
    enhancedLogging: false,
    logFunctionDuration: false,
  },
  diagnostics: {
    iconMapping: false,
    imageTransform: false,
  },
};

// Load console log settings from the database
export const loadConsoleSettings = async (): Promise<ConsoleLogsSettings> => {
  if (cachedSettings) {
    return cachedSettings;
  }

  if (settingsLoadPromise) {
    return settingsLoadPromise;
  }

  settingsLoadPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('settings')
        .eq('id', 1)
        .single();

      if (error || !data) {
        // Return default settings if error or no data
        return defaultConsoleSettings;
      }

      const settings = data.settings as any;
      const consoleSettings = settings.consoleLogs || defaultConsoleSettings;

      cachedSettings = consoleSettings;
      return consoleSettings;
    } catch (error) {
      console.error('Error loading console settings:', error);
      return defaultConsoleSettings;
    }
  })();

  return settingsLoadPromise;
};

// Clear the cache when settings are updated
export const clearConsoleSettingsCache = () => {
  cachedSettings = null;
  settingsLoadPromise = null;
};

// Console logging functions
export const logIndexComponentRendering = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.componentRendering) {
    console.log(`[Index Component] ${message}`, data || '');
  }
};

export const logIndexAuthFlow = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.authFlow) {
    console.log(`[Index Auth] ${message}`, data || '');
  }
};

export const logIndexNetworkStatus = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.networkStatus) {
    console.log(`[Index Network] ${message}`, data || '');
  }
};

export const logIndexTermsCheck = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.termsCheck) {
    console.log(`[Index Terms] ${message}`, data || '');
  }
};

export const logIndexErrorHandling = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.errorHandling) {
    console.error(`[Index Error] ${message}`, data || '');
  }
};

export const logIndexPerformanceMetrics = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.indexPage.performanceMetrics) {
    console.log(`[Index Performance] ${message}`, data || '');
  }
};

export const logPerformanceMonitor = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.performance?.performanceMonitor) {
    console.log(`[Performance Monitor] ${message}`, data || '');
  }
};

export const logFontLoadingMonitor = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.performance?.fontLoading) {
    console.log(`[Font Loading] ${message}`, data || '');
  }
};

export const logAuthContext = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.auth?.authContext) {
    console.log(`[Auth Context] ${message}`, data || '');
  }
};

export const logAdminSettings = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.admin?.useAdminSettings) {
    console.log(`[Admin Settings] ${message}`, data || '');
  }
};

export const logIconMapping = async (message: string, data?: any) => {
  const settings = await loadConsoleSettings();
  if (settings.diagnostics?.iconMapping) {
    console.log(`[Icon Mapping] ${message}`, data || '');
  }
};

export const logImageTransform = async (message: string, data?: any, level: 'log' | 'error' = 'log') => {
  const settings = await loadConsoleSettings();
  if (settings.diagnostics?.imageTransform) {
    if (level === 'error') {
      console.error(`[Image Transform] ${message}`, data || '');
    } else {
      console.log(`[Image Transform] ${message}`, data || '');
    }
  }
};
